import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { RootState, useAppDispatch } from '../store';
import { StockData } from '../types';
import { setChartTicker } from '../store/slices/chartSlice';
import {
  requestNavigateMainTab,
  requestDeepSubTab,
  setDeepAnalysisReturnTab,
} from '../store/slices/modalsSlice';
import { setSelectedTicker } from '../store/slices/analysisPanelSlice';
import { isChartsGridEnabled } from '../config/featureFlags';
import { addFavoriteThunk } from '../store/thunks';
import {
  selectStocks,
  selectFavoritesLoading,
  selectIsTickerFavorited,
} from '../store/selectors';
import { cn } from '../lib/utils';
import { getTradingViewExchangePrefix } from '../utils/tradingViewExchange';
import { buildTradingViewMaStudies } from '../constants/chartColors';
import { useTheme } from '../contexts/ThemeContext';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
} from './ui/Dialog';
import { Button } from './ui/Button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/Tabs';
import { Input } from './ui/Input';
import { Label } from './ui/Label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/Select';
import { Textarea } from './ui/Textarea';
import {
  BarChart3,
  LineChart,
  Star,
  Loader2,
  TrendingUp,
  Microscope,
} from 'lucide-react';

interface StockActionModalProps {
  ticker: string;
  show: boolean;
  onHide: () => void;
}

const StockActionModal: React.FC<StockActionModalProps> = React.memo(
  ({ ticker, show, onHide }) => {
    const dispatch = useAppDispatch();
    const stocks = useSelector(selectStocks);
    const favoritesLoading = useSelector(selectFavoritesLoading);
    const isTickerFavorited = useSelector((state: RootState) =>
      selectIsTickerFavorited(state, ticker)
    );
    const filters = useSelector((state: RootState) => state.filters);
    const { theme: themeMode } = useTheme();
    const [activeTab, setActiveTab] = useState<'tradingview'>('tradingview');
    const [favoriteModal, setFavoriteModal] = useState<{ show: boolean }>({
      show: false,
    });
    const [favoriteColor, setFavoriteColor] = useState('blue');
    const [favoriteNotes, setFavoriteNotes] = useState('');
    const widgetRef = useRef<any>(null);

    const getExchangeForTicker = useCallback(
      (ticker: string) => {
        const row = stocks.find((s: StockData) => s.ticker === ticker);
        return getTradingViewExchangePrefix({
          ticker,
          exchange: row?.exchange,
        });
      },
      [stocks]
    );

    const createWidget = useCallback(
      (ticker: string, exchange: string, container: HTMLElement) => {
        try {
          const containerRect = container.getBoundingClientRect();
          const containerWidth = Math.max(containerRect.width || 800, 800);
          const containerHeight = Math.max(containerRect.height || 500, 500);

          const isWeekly = filters.ma_timeframe_mode === 'WEEKLY';
          const { studies, studiesOverrides } = buildTradingViewMaStudies(
            filters.ma_filter_selection,
            themeMode
          );

          window.tvWidget = new window.TradingView.widget({
            autosize: false,
            width: containerWidth,
            height: containerHeight,
            symbol: exchange ? `${exchange}:${ticker}` : ticker,
            interval: isWeekly ? 'W' : 'D',
            timezone: 'Etc/UTC',
            theme: themeMode === 'light' ? 'light' : 'dark',
            style: '1',
            locale: 'en',
            toolbar_bg: themeMode === 'light' ? '#f3f4f6' : '#1a1d24',
            enable_publishing: false,
            allow_symbol_change: true,
            container_id: 'tradingview_widget_action_modal',
            hide_top_toolbar: false,
            hide_legend: false,
            hide_side_toolbar: false,
            save_image: false,
            withdateranges: true,
            hide_volume: false,
            studies: studies,
            studies_overrides: studiesOverrides,
            onready: function () {
              console.log(
                `TradingView widget loaded with ${studies.length} moving average(s)`
              );
            },
          });
        } catch (error) {
          console.error('Error creating TradingView widget:', error);
        }
      },
      [filters.ma_filter_selection, filters.ma_timeframe_mode, themeMode]
    );

    const tvDebounceTimerRef = useRef<ReturnType<typeof setTimeout>>();
    const initializeTradingViewWidget = useCallback(
      (ticker: string) => {
        clearTimeout(tvDebounceTimerRef.current);
        tvDebounceTimerRef.current = setTimeout(() => {
          const container = document.getElementById(
            'tradingview_widget_action_modal'
          );
          if (!container) return;

          // Theme toggle must force re-creation: TV widget cannot hot-swap theme.
          if (widgetRef.current?.ticker === ticker && widgetRef.current?.theme === themeMode) {
            return;
          }

          const exchange = getExchangeForTicker(ticker);
          container.innerHTML = '';

          // Add loading indicator
          container.innerHTML = `
            <div class="flex justify-center items-center h-full">
              <div class="text-center">
                <div class="w-8 h-8 border-2 border-accent-primary border-t-transparent rounded-full animate-spin mx-auto"></div>
                <p class="mt-2 text-text-muted">Loading TradingView Chart...</p>
              </div>
            </div>
          `;

          if (widgetRef.current?.widget) {
            try {
              widgetRef.current.widget.remove();
            } catch (e) {
              console.log('Error removing existing widget:', e);
            }
          }

          const initWidget = () => {
            try {
              const widget = createWidget(
                ticker,
                exchange,
                container as HTMLElement
              );
              widgetRef.current = {
                widget,
                ticker,
                exchange,
                theme: themeMode,
              };
            } catch (error) {
              console.error('Widget initialization failed:', error);
            }
          };

          if (!window.TradingView) {
            const existingScript = document.querySelector(
              'script[src="https://s3.tradingview.com/tv.js"]'
            );
            if (!existingScript) {
              const script = document.createElement('script');
              script.src = 'https://s3.tradingview.com/tv.js';
              script.async = true;
              script.onload = () => {
                setTimeout(initWidget, 100);
              };
              script.onerror = () => {
                container.innerHTML =
                  '<div class="p-4 text-accent-bearish">Error loading TradingView widget</div>';
              };
              document.head.appendChild(script);
            } else {
              const checkTradingView = () => {
                if (window.TradingView) {
                  initWidget();
                } else {
                  setTimeout(checkTradingView, 100);
                }
              };
              checkTradingView();
            }
          } else {
            initWidget();
          }
        }, 300);
      },
      [getExchangeForTicker, createWidget, themeMode]
    );

    useEffect(() => {
      if (show && ticker) {
        dispatch(setChartTicker(ticker));
      }
    }, [show, ticker, dispatch]);

    useEffect(() => {
      if (show && ticker && activeTab === 'tradingview') {
        setTimeout(() => {
          initializeTradingViewWidget(ticker);
        }, 100);
      }
    }, [activeTab, show, ticker, initializeTradingViewWidget]);

    // Cleanup on unmount or modal close
    useEffect(() => {
      if (!show) {
        setActiveTab('tradingview');
        if (window.tvWidget) {
          try {
            window.tvWidget.remove();
            window.tvWidget = null;
          } catch (e) {
            // Ignore cleanup errors
          }
        }
      }
    }, [show]);

    const handleAddToFavorites = useCallback(() => {
      if (favoritesLoading) {
        alert('Please wait while favorites are loading...');
        return;
      }

      if (isTickerFavorited) {
        alert(`${ticker} is already in your favorites!`);
        return;
      }

      setFavoriteModal({ show: true });
      setFavoriteColor('blue');
      setFavoriteNotes(`Added from Stock Action for ${ticker}`);
    }, [favoritesLoading, isTickerFavorited, ticker]);

    const confirmAddToFavorites = useCallback(async () => {
      try {
        await dispatch(
          addFavoriteThunk({
            ticker,
            color: favoriteColor,
            notes: favoriteNotes,
          })
        ).unwrap();
        setFavoriteModal({ show: false });
      } catch (error: any) {
        console.error('Error adding to favorites:', error);

        if (error.message && error.message.includes('already in favorites')) {
          alert(`${ticker} is already in your favorites!`);
        } else if (error.message && error.message.includes('409')) {
          alert(`${ticker} is already in your favorites!`);
        } else {
          alert(`Failed to add ${ticker} to favorites. Please try again.`);
        }

        setFavoriteModal({ show: false });
      }
    }, [dispatch, ticker, favoriteColor, favoriteNotes]);

    const openTradingViewLink = (ticker: string) => {
      const exchange = getExchangeForTicker(ticker);
      const interval =
        filters.ma_timeframe_mode === 'WEEKLY' ? '&interval=W' : '';
      window.open(
        `https://www.tradingview.com/chart/?symbol=${exchange ? `${exchange}:${ticker}` : ticker}${interval}`,
        '_blank'
      );
    };

    const handleOpenSelectedChartTab = () => {
      if (!isChartsGridEnabled()) {
        openTradingViewLink(ticker);
        return;
      }
      dispatch(setChartTicker(ticker));
      dispatch(setSelectedTicker(ticker));
      dispatch(requestDeepSubTab('chart'));
      dispatch(requestNavigateMainTab('deep_analysis'));
      onHide();
    };

    if (!show) return null;

    return (
      <>
        <Dialog open={show} onOpenChange={(open) => !open && onHide()}>
          <DialogContent size="full" className="w-[95vw] h-[90vh]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-3 whitespace-nowrap">
                <TrendingUp className="h-5 w-5 text-accent-primary" />
                {ticker} - Stock Analysis
              </DialogTitle>
              <div className="absolute right-16 top-4 flex items-center gap-2">
                <Button
                  size="sm"
                  variant={
                    favoritesLoading
                      ? 'secondary'
                      : isTickerFavorited
                        ? 'success'
                        : 'outline'
                  }
                  onClick={handleAddToFavorites}
                  disabled={favoritesLoading}
                  title={
                    favoritesLoading
                      ? 'Loading favorites...'
                      : isTickerFavorited
                        ? `${ticker} is already in favorites`
                        : 'Add to Favorites'
                  }
                >
                  {favoritesLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Star
                      className={cn(
                        'h-4 w-4',
                        isTickerFavorited && 'fill-current'
                      )}
                    />
                  )}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleOpenSelectedChartTab}
                  title={
                    isChartsGridEnabled()
                      ? 'Open selected chart (full tab)'
                      : 'Open in TradingView'
                  }
                >
                  <LineChart className="h-4 w-4" />
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    dispatch(setSelectedTicker(ticker));
                    dispatch(setChartTicker(ticker));
                    dispatch(setDeepAnalysisReturnTab('screening'));
                    dispatch(requestDeepSubTab('analysis'));
                    dispatch(requestNavigateMainTab('deep_analysis'));
                    onHide();
                  }}
                  title="Deep Analysis - patterns, indicators, AI"
                >
                  <Microscope className="h-4 w-4" />
                </Button>
              </div>
            </DialogHeader>

            <DialogBody className="p-0">
              <Tabs
                value={activeTab}
                onValueChange={(v) => setActiveTab(v as any)}
                className="h-full flex flex-col"
              >
                <div className="px-6 pt-2 border-b border-border-subtle">
                  <TabsList>
                    <TabsTrigger
                      value="tradingview"
                      className="flex items-center gap-2"
                    >
                      <BarChart3 className="h-4 w-4" />
                      TradingView
                    </TabsTrigger>
                    {/* AI tab removed - AI analysis available only via Deep Analysis tab */}
                  </TabsList>
                </div>

                <div className="flex-1 overflow-auto">
                  {/* TradingView Tab */}
                  <TabsContent
                    value="tradingview"
                    className="h-full relative"
                    forceMount
                    style={{
                      display: activeTab === 'tradingview' ? 'block' : 'none',
                    }}
                  >
                    <div
                      id="tradingview_widget_action_modal"
                      className="w-full h-[600px]"
                    />
                  </TabsContent>

                  {/* AI Analysis Tab removed - AI analysis available only via Deep Analysis tab */}
                </div>
              </Tabs>
            </DialogBody>

            <DialogFooter>
              <Button variant="secondary" onClick={onHide}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Favorite Modal */}
        <Dialog
          open={favoriteModal.show}
          onOpenChange={(open) => !open && setFavoriteModal({ show: false })}
        >
          <DialogContent size="sm">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 whitespace-nowrap">
                <Star className="h-5 w-5 text-accent-warning" />
                Add to Favorites
              </DialogTitle>
            </DialogHeader>
            <DialogBody>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="ticker-input">Stock Ticker</Label>
                  <Input
                    id="ticker-input"
                    value={ticker}
                    readOnly
                    className="bg-surface-container"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="color-select">Priority Color</Label>
                  <Select
                    value={favoriteColor}
                    onValueChange={setFavoriteColor}
                  >
                    <SelectTrigger id="color-select">
                      <SelectValue placeholder="Select a color" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="blue">
                        <span className="flex items-center gap-2">
                          <span className="w-3 h-3 rounded-full bg-accent-primary" />
                          Gold - Normal Priority
                        </span>
                      </SelectItem>
                      <SelectItem value="green">
                        <span className="flex items-center gap-2">
                          <span className="w-3 h-3 rounded-full bg-green-500"></span>
                          Green - High Priority
                        </span>
                      </SelectItem>
                      <SelectItem value="yellow">
                        <span className="flex items-center gap-2">
                          <span className="w-3 h-3 rounded-full bg-yellow-500"></span>
                          Yellow - Medium Priority
                        </span>
                      </SelectItem>
                      <SelectItem value="red">
                        <span className="flex items-center gap-2">
                          <span className="w-3 h-3 rounded-full bg-red-500"></span>
                          Red - Critical Priority
                        </span>
                      </SelectItem>
                      <SelectItem value="gray">
                        <span className="flex items-center gap-2">
                          <span className="w-3 h-3 rounded-full bg-gray-500"></span>
                          Gray - Low Priority
                        </span>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="notes-input">Notes</Label>
                  <Textarea
                    id="notes-input"
                    value={favoriteNotes}
                    onChange={(e) => setFavoriteNotes(e.target.value)}
                    placeholder="Optional notes about this favorite..."
                    rows={3}
                  />
                </div>
              </div>
            </DialogBody>
            <DialogFooter>
              <Button
                variant="secondary"
                onClick={() => {
                  setFavoriteModal({ show: false });
                  setFavoriteColor('blue');
                  setFavoriteNotes('');
                }}
              >
                Cancel
              </Button>
              <Button onClick={confirmAddToFavorites}>
                <Star className="h-4 w-4 mr-2" />
                Add to Favorites
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </>
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.ticker === nextProps.ticker &&
      prevProps.show === nextProps.show &&
      prevProps.onHide === nextProps.onHide
    );
  }
);

StockActionModal.displayName = 'StockActionModal';

export default StockActionModal;
